import {
  trackViewAPI,
  trackShareAPI,
  trackPurchaseAPI,
} from "../api/analyticsApi";
import {
  trackCheckoutStarted,
  trackContentViewed,
  trackShareClicked,
  trackShareGenerated,
} from "../analytics/canonicalTracking";

// Get user's location info (simplified)
const getUserLocation = () => {
  // This is a simplified version - in production you might want to use a geolocation service
  return {
    country: "US", // Default fallback
    city: "Unknown",
  };
};

const getCurrentUserContext = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return { userId: undefined, userRole: "guest" };
    const parsed = JSON.parse(raw);
    const role = parsed?.userType?.title || "guest";
    return { userId: parsed?.id, userRole: role };
  } catch {
    return { userId: undefined, userRole: "guest" };
  }
};

// Get device type
const getDeviceType = () => {
  const userAgent = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
    return "tablet";
  }
  if (
    /mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(
      userAgent
    )
  ) {
    return "mobile";
  }
  return "desktop";
};

// Track view analytics
export const trackView = async (
  contentId: string,
  contentType: "photo" | "video" | "post",
  additionalData: any = {}
) => {
  try {
    const { userId, userRole } = getCurrentUserContext();
    const location = getUserLocation();
    const deviceType = getDeviceType();

    // Map frontend values to backend enum values based on actual schema
    const contentTypeMap: { [key: string]: string } = {
      photo: "gallery", // Backend uses 'gallery' for photos
      video: "video",
      post: "post",
    };

    const deviceTypeMap: { [key: string]: string } = {
      mobile: "mobile",
      tablet: "tablet",
      desktop: "desktop",
    };

    const viewData = {
      contentId,
      contentType: contentTypeMap[contentType] || "post",
      viewDuration: additionalData.viewDuration || 0,
      isCompleteView: additionalData.isCompleteView || false,
      referrer: document.referrer || "direct",
      deviceType: deviceTypeMap[deviceType] || "desktop",
      country: location.country,
      city: location.city,
      ...additionalData,
    };

    await trackViewAPI(viewData);

    trackContentViewed({
      view_type: contentType === "post" ? "post" : contentType,
      user_id: userId,
      user_role: userRole,
      post_id: contentId,
      content_type: contentType === "post" ? "mixed" : contentType,
      watch_time_seconds: additionalData.viewDuration || 0,
      mvp_feature:
        contentType === "post" ? "content_view_post" : "content_view_media",
    });
  } catch (error) {
    console.error("Error tracking view:", error);
  }
};

// Track share analytics
export const trackShare = async (
  contentId: string,
  contentType: "photo" | "video" | "post",
  shareType: "link" | "embed" | "download",
  sharePlatform: string,
  additionalData: any = {}
) => {
  try {
    const { userId, userRole } = getCurrentUserContext();
    // Map frontend values to backend enum values based on actual schema
    const shareTypeMap: { [key: string]: string } = {
      link: "direct", // Maps to 'direct' in backend
      embed: "embed",
      download: "download",
    };

    const sharePlatformMap: { [key: string]: string } = {
      native_share: "direct", // Native share maps to 'direct'
      clipboard: "copy_link", // Clipboard maps to 'copy_link'
      facebook: "facebook",
      twitter: "twitter",
      instagram: "instagram",
      whatsapp: "whatsapp",
      telegram: "telegram",
      email: "email",
      sms: "sms",
      other: "direct", // Fallback to 'direct'
    };

    const shareData = {
      contentId,
      contentType,
      shareType: shareTypeMap[shareType] || "direct",
      sharePlatform: sharePlatformMap[sharePlatform] || "direct",
      shareUrl: additionalData.shareUrl || window.location.href,
      shareText: additionalData.shareText || "",
      shareMetadata: additionalData.shareMetadata || {},
      ...additionalData,
    };

    await trackShareAPI(shareData);

    const surface = additionalData?.surface as string | undefined;
    trackShareClicked({
      share_target: contentType === "post" ? "post" : "profile",
      user_id: userId,
      post_id: contentId,
      user_role: userRole,
      mvp_feature:
        additionalData?.mvp_feature_intent ??
        (contentType === "post" ? "share_post_intent" : "share_profile_intent"),
      surface,
    });
    trackShareGenerated({
      share_target: contentType === "post" ? "post" : "profile",
      share_format: "link",
      share_method:
        sharePlatform === "clipboard"
          ? "copy_link"
          : sharePlatform === "native_share"
            ? "native_share"
            : "copy_link",
      user_id: userId,
      post_id: contentId,
      user_role: userRole,
      mvp_feature:
        additionalData?.mvp_feature_generated ??
        (contentType === "post" ? "share_post_link" : "share_profile_link"),
      surface,
    });
  } catch (error) {
    console.error("Error tracking share:", error);
  }
};

// Track purchase analytics
export const trackPurchase = async (
  contentId: string,
  contentType: "photo" | "video" | "post",
  purchaseType: "tip" | "subscription" | "custom_video" | "post_purchase",
  amount: number,
  currency: string = "USD",
  additionalData: any = {}
) => {
  try {
    const { userId, userRole } = getCurrentUserContext();
    const canonicalPurchaseType =
      purchaseType === "post_purchase" ? "ppv" : purchaseType;
    trackCheckoutStarted({
      purchase_type: canonicalPurchaseType as
        | "subscription"
        | "ppv"
        | "tip"
        | "custom_video",
      value: amount,
      post_id: contentId,
      user_id: userId,
      user_role: userRole,
      mvp_feature: `checkout_${canonicalPurchaseType}`,
    });

    // Map frontend values to backend enum values
    const purchaseTypeMap: { [key: string]: string } = {
      tip: "tip",
      subscription: "subscription",
      custom_video: "custom_video",
      post_purchase: "post_purchase",
    };

    const paymentMethodMap: { [key: string]: string } = {
      credit_card: "credit_card",
      debit_card: "debit_card",
      paypal: "paypal",
      stripe: "stripe",
      ccbill: "ccbill",
      crypto: "crypto",
      bank_transfer: "bank_transfer",
      unknown: "unknown",
    };

    const paymentStatusMap: { [key: string]: string } = {
      pending: "pending",
      completed: "completed",
      failed: "failed",
      cancelled: "cancelled",
      refunded: "refunded",
    };

    const purchaseData = {
      contentId,
      contentType,
      purchaseType: purchaseTypeMap[purchaseType] || "tip",
      amount,
      currency,
      paymentMethod:
        paymentMethodMap[additionalData.paymentMethod] || "unknown",
      paymentStatus:
        paymentStatusMap[additionalData.paymentStatus] || "completed",
      transactionId: additionalData.transactionId || "",
      purchaseMetadata: additionalData.purchaseMetadata || {},
      ...additionalData,
    };

    await trackPurchaseAPI(purchaseData);
  } catch (error) {
    console.error("Error tracking purchase:", error);
  }
};

// Helper function to track post view when modal opens
export const trackPostView = (postId: string, postType: "photo" | "video") => {
  trackView(postId, postType, {
    isCompleteView: false, // Will be updated when user closes modal
  });
};

// Helper function to track complete post view when modal closes
export const trackCompletePostView = (
  postId: string,
  postType: "photo" | "video",
  viewDuration: number
) => {
  trackView(postId, postType, {
    isCompleteView: true,
    viewDuration,
  });
};
